import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import Validator, { useValidate, checkValidity } from '../../../components/validator';
import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    formWidth: {
        minWidth: '400px',
    },
    select: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
}));

const Support = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings, setSystemSettings } = useContext(DataContext);
    const [support, setSupport] = useState({
        key: '',
        backtesting: {
            ui: false,
        },
    });
    const [original, setOriginal] = useState();
    const [changes, setChanges] = useState(false);
    const supportForm = useValidate({});
    const supportPerms = Auth.permissions.get('Support');

    useEffect(() => {
        setChanges(_isEqual(support, original));
    }, [support, original]);

    useEffect(() => {
        if (systemSettings && systemSettings.support) {
            const s = _cloneDeep(systemSettings.support);
            if (!s.key) {
                s.key = '';
            }
            if (!s.backtesting) {
                s.backtesting = {
                    ui: false,
                };
            }
            setSupport(s);
            setOriginal(systemSettings.support);
        }
    }, [systemSettings]);

    const handleSupportKey = (e, validID, valid) => {
        supportForm.set({ ...supportForm.get(), [validID]: valid });

        setSupport({
            ...support,
            key: e.target.value,
        });
    };

    const handleUpdateBTUI = (e) => {
        const s = { ...support };
        s.backtesting.ui = e.target.checked;
        setSupport(s);
    };

    const handleSave = () => {
        updateSystemSettings({ support });
        setSystemSettings({
            ...systemSettings,
            support: support,
        });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    return (
        <Grid container>
            <Grid item lg={6}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={5}>
                            <Grid item xs={12}>
                                <Typography variant="h6">Support Key</Typography>
                                <Validator
                                    valueType="supportKey"
                                    errorText="Key must be 20 character hexadecimal value"
                                    allowEmpty
                                    type="string"
                                    inputProps={{
                                        min: 20,
                                        max: 20,
                                    }}
                                    label="OpenText™ NDR Support Key"
                                    aria-label="support key"
                                    fullWidth
                                    margin="normal"
                                    disabled={!supportPerms.write}
                                    value={support.key}
                                    onChange={handleSupportKey}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display={ _get(systemSettings, 'support.backtesting.exists', false) ? 'block' : 'none' }>
                                    <Typography variant="h6">Backtesting</Typography>
                                    <FormControlLabel
                                        labelPlacement="start"
                                        label="Enable Backtesting UI"
                                        control={<Checkbox checked={support.backtesting.ui} />}
                                        onChange={handleUpdateBTUI}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                {supportPerms.write && <Button
                                    className={classes.buttonRight}
                                    variant="contained"
                                    color="primary"
                                    aria-label="save"
                                    onClick={handleSave}
                                    disabled={changes || !checkValidity(supportForm.get())}
                                >
                                    Save
                                </Button>}
                                {supportPerms.write && <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Support;
